// Client API — déploiements d'instances client Scaleway (super_admin).

const BASE_URL = import.meta.env.VITE_WATTELZCE_API_URL || '';

export type DeploymentStatus =
  | 'pending'
  | 'provisioning'
  | 'seeding'
  | 'ready'
  | 'failed'
  | 'destroying'
  | 'destroyed';

export interface ClientDeployment {
  id: number;
  org_id: number;
  org_name?: string | null;
  slug: string;
  target: string;
  notify_email: string | null;
  status: DeploymentStatus;
  region: string;
  db_node_type: string;
  scaleway_project_id: string | null;
  backend_url: string | null;
  frontend_url: string | null;
  db_instance_id: string | null;
  archive_object: string | null;
  notified_at: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateDeploymentPayload {
  orgId: number;
  notifyEmail: string;
  target?: string;
  region?: string;
  dbNodeType?: string;
  // Push SSH (target vps/linux) — identifiants ÉPHÉMÈRES, jamais stockés.
  sshHost?: string;
  sshPort?: number;
  sshUser?: string;
  /** Contenu du fichier de clé privée, encodé en base64. */
  sshKeyB64?: string;
  httpPort?: number;
  // Cloud cible GCP — identifiants DU CLIENT, ÉPHÉMÈRES, jamais stockés.
  /** Contenu de la clé JSON du compte de service GCP, encodé en base64. */
  gcpCredentialsB64?: string;
  gcpProject?: string;
  gcpRegion?: string;
  gcpZone?: string;
  machineType?: string;
}

function getHeaders(): HeadersInit {
  const token = localStorage.getItem('dashboard_zce_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: { ...getHeaders(), ...(options?.headers || {}) },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    const rawErr = json.error;
    const msg = (typeof rawErr === 'string' ? rawErr : rawErr?.message) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return json as T;
}

export interface OrgOption {
  id: number;
  name: string;
  slug: string;
  status: string;
}

export const deploymentsApi = {
  // Organisations existantes (pour le sélecteur du formulaire).
  listOrganizations: () =>
    apiFetch<{ success: true; clients: OrgOption[] }>('/api/admin/clients').then((r) => r.clients),

  list: () =>
    apiFetch<{ success: true; deployments: ClientDeployment[] }>('/api/admin/deployments').then(
      (r) => r.deployments,
    ),

  get: (id: number) =>
    apiFetch<{ success: true; deployment: ClientDeployment }>(`/api/admin/deployments/${id}`).then(
      (r) => r.deployment,
    ),

  create: (payload: CreateDeploymentPayload) =>
    apiFetch<{ success: true; deploymentId: number; status: DeploymentStatus }>(
      '/api/admin/deployments',
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  remove: (id: number) =>
    apiFetch<{ success: true; status: DeploymentStatus; note?: string }>(`/api/admin/deployments/${id}`, {
      method: 'DELETE',
    }),

  // Renvoi manuel de l'e-mail « application prête » (lien seul).
  notify: (id: number) =>
    apiFetch<{ success: true }>(`/api/admin/deployments/${id}/notify`, { method: 'POST' }),

  // Lien pré-signé (1 h) vers l'archive de déploiement.
  archiveUrl: (id: number) =>
    apiFetch<{ success: true; url: string }>(`/api/admin/deployments/${id}/archive-url`).then((r) => r.url),
};
